import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { 
  UserApiKeysService, 
  UserApiKeySummary, 
  CreateUserApiKeyResponse 
} from '../../core/service/user-api-keys.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-api-keys-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './api-keys-modal.component.html',
  styleUrls: ['./api-keys-modal.component.scss']
})
export class ApiKeysModalComponent implements OnInit, OnDestroy {
  isOpen = false;
  apiKeys: UserApiKeySummary[] = [];
  isLoading = false;
  error: string | null = null;
  
  showCreateForm = false;
  newKeyName = '';
  newKeyDescription = '';
  createdKey: CreateUserApiKeyResponse | null = null;
  showCreatedKey = false;
  copied = false;
  
  editingKeyId: string | null = null;
  editForm: { name: string; description: string; enabled: boolean } = {
    name: '',
    description: '',
    enabled: true
  };
  
  private subscription = new Subscription();
  private copyTimeout: any;

  constructor(private apiKeysService: UserApiKeysService) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  open(): void {
    this.isOpen = true;
    this.loadApiKeys();
  }

  close(): void {
    this.isOpen = false;
    this.showCreateForm = false;
    this.showCreatedKey = false;
    this.createdKey = null;
    this.editingKeyId = null;
    this.error = null;
  }

  loadApiKeys(): void {
    this.isLoading = true;
    this.error = null;
    
    this.subscription.add(
      this.apiKeysService.listApiKeys().subscribe({
        next: (keys) => {
          this.apiKeys = keys;
          this.isLoading = false;
        },
        error: (err) => {
          this.error = 'Failed to load API keys';
          this.isLoading = false;
          console.error('Error loading API keys:', err);
        }
      })
    );
  }

  openCreateForm(): void {
    this.showCreateForm = true;
    this.newKeyName = '';
    this.newKeyDescription = '';
  }

  cancelCreate(): void {
    this.showCreateForm = false;
    this.newKeyName = '';
    this.newKeyDescription = '';
  }

  createApiKey(): void {
    this.isLoading = true;
    this.error = null;
    
    this.subscription.add(
      this.apiKeysService.createApiKey(this.newKeyName || undefined, this.newKeyDescription || undefined).subscribe({
        next: (response) => {
          this.createdKey = response;
          this.showCreatedKey = true;
          this.showCreateForm = false;
          this.isLoading = false;
          this.loadApiKeys();
        },
        error: (err) => {
          this.error = 'Failed to create API key';
          this.isLoading = false;
          console.error('Error creating API key:', err);
        }
      })
    );
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.copied = true;
      if (this.copyTimeout) {
        clearTimeout(this.copyTimeout);
      }
      this.copyTimeout = setTimeout(() => {
        this.copied = false;
      }, 2000);
    });
  }

  closeCreatedKeyModal(): void {
    this.showCreatedKey = false;
    this.createdKey = null;
  }

  startEdit(key: UserApiKeySummary): void {
    this.editingKeyId = key.id;
    this.editForm = {
      name: key.name,
      description: key.description,
      enabled: key.enabled
    };
  }

  cancelEdit(): void {
    this.editingKeyId = null;
  }

  saveEdit(keyId: string): void {
    this.isLoading = true;
    this.error = null;
    
    this.subscription.add(
      this.apiKeysService.updateApiKey(keyId, this.editForm).subscribe({
        next: () => {
          this.editingKeyId = null;
          this.isLoading = false;
          this.loadApiKeys();
        },
        error: (err) => {
          this.error = 'Failed to update API key';
          this.isLoading = false;
          console.error('Error updating API key:', err);
        }
      })
    );
  }

  deleteApiKey(keyId: string): void {
    if (!confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }
    
    this.isLoading = true;
    this.error = null;
    
    this.subscription.add(
      this.apiKeysService.deleteApiKey(keyId).subscribe({
        next: () => {
          this.isLoading = false;
          this.loadApiKeys();
        },
        error: (err) => {
          this.error = 'Failed to delete API key';
          this.isLoading = false;
          console.error('Error deleting API key:', err);
        }
      })
    );
  }

  formatDate(timestamp: number): string {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  toggleKeyStatus(key: UserApiKeySummary): void {
    this.isLoading = true;
    this.error = null;
    
    this.subscription.add(
      this.apiKeysService.updateApiKey(key.id, { enabled: !key.enabled }).subscribe({
        next: () => {
          this.isLoading = false;
          this.loadApiKeys();
        },
        error: (err) => {
          this.error = 'Failed to update API key status';
          this.isLoading = false;
          console.error('Error updating API key status:', err);
        }
      })
    );
  }
}

